import { Collapse, useModal } from "@newsela/angelou";
import { useState } from "react";

import useLessonPlannerVisibility from "@/assignments/useLessonPlannerVisibility";
import useAssignmentPreview from "@/interactiveVideos/AssignmentPreview/assignmentPreview";
import { StandardsModalProvider } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModalProvider";
import { StandardsSearchProvider } from "@/search/Langston/StandardsSearchProvider";
import { IStandard } from "@/search/Langston/types/IStandard";
import { LangstonContentResponse } from "@/types/models";
import ContentLevel from "@/types/models/ContentLevel.interface";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import { isUnsupportedContentForTeacherTools } from "@/utils/generatedContent";
import { Metadata } from "@/view/ContentHead";
import { isArticle } from "@/view/utils/contentTypeChecks";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import { LessonPlannerStandardsModal } from "./LessonPlannerTool/LessonPlannerStandards/LessonPlannerStandardsModal";
import { bodyCss, expanderContentCss, rootCss } from "./TeacherTools.style";
import TeacherToolsCollapseHeader from "./TeacherToolsCollapseHeader";
import TeacherToolsTabs from "./TeacherToolsTabs";

interface TeacherToolsProps {
  content: LangstonContentResponse | null | undefined;
  contentMetadata?: Metadata[] | null | undefined;
  currentLevel: ContentLevel | null | undefined;
}

const TeacherTools = ({
  content,
  contentMetadata,
  currentLevel,
}: TeacherToolsProps) => {
  const { isPresentModeActive } = usePresentModeContext();
  const isAssignmentPreview = useAssignmentPreview();
  const { isTeacher, isLiteUser, canSeeAiFeatures } = useGetUserProperties();

  const contentData = content?.data;
  const contentType = content?.content_type;
  const isArticleData = isArticle(contentData);
  const articleType = isArticleData ? contentData.article_type : undefined;
  const canViewLessonPlanner = useLessonPlannerVisibility({
    contentType,
    articleType,
    contentMetadata,
    currentLevel,
  });

  const { isStudentPreviewOn } = useStudentPreviewContext();
  const [selectedStandards, setSelectedStandards] = useState<IStandard[]>([]);
  const [standardsModalProps, setIsStandardsModalActive] = useModal(false);

  const {
    isHidden: isTeacherToolsCollapsed,
    onClick: setIsTeacherToolsCollapsed,
  } = Collapse.useCollapse({ isHidden: false });

  const isVeiled = content?.data?.is_premium && isLiteUser;

  // No need to process article when the user doesn't have access to the feature.
  const skipAssetGeneration =
    !isTeacher ||
    isVeiled ||
    isUnsupportedContentForTeacherTools({
      articleType,
      contentType,
      text: currentLevel?.text || "",
      contentMetadata,
    });

  if (
    !canSeeAiFeatures ||
    isPresentModeActive ||
    isStudentPreviewOn ||
    isAssignmentPreview ||
    skipAssetGeneration
  ) {
    return null;
  }

  const renderHeader = () => (
    <TeacherToolsCollapseHeader
      isCollapsed={isTeacherToolsCollapsed}
      handleCollapse={setIsTeacherToolsCollapsed}
    />
  );

  return (
    <StandardsSearchProvider>
      <StandardsModalProvider>
        <div css={rootCss(isTeacherToolsCollapsed)}>
          <Collapse
            isHidden={isTeacherToolsCollapsed}
            renderCustomTrigger={renderHeader}
            __cssFor={{
              expanderContent: expanderContentCss,
            }}
          >
            <div css={bodyCss}>
              <TeacherToolsTabs
                canViewLessonPlanner={canViewLessonPlanner}
                selectedStandards={selectedStandards}
                setIsStandardsModalActive={setIsStandardsModalActive}
                setSelectedStandards={setSelectedStandards}
              />
            </div>
          </Collapse>
          <LessonPlannerStandardsModal
            modalProps={standardsModalProps}
            setStandardsModalActive={setIsStandardsModalActive}
            setSelectedStandards={setSelectedStandards}
          />
        </div>
      </StandardsModalProvider>
    </StandardsSearchProvider>
  );
};

export default TeacherTools;
